import { useState, useEffect, useCallback, useRef } from 'react';
import { useLicenseStore } from '../store/licenseStore';
import { useAuthStore } from '../store/authStore';
import { useTrialPromptStore } from '../store/trialPromptStore';
import { isBrowserMode } from '../../../features/browser-mode/browserModeUtils';
import { startServerTrial } from '../services/entitlementTokenService';
import { isAuthConfigured } from '../services/authService';
import { isTrialOnboardingDue, shouldReofferTrialAfterSignIn } from '../services/trialService';
import {
  clearPendingTrialIntent,
  consumeFreshTrialReoffer,
  recordAuthIntentSource,
  recordPendingTrialIntent,
} from '../services/signInIntent';
import { useHasProAccess } from './useHasProAccess';
import { useProAccessReady } from './useProAccessReady';
import { trackTrialRequested } from '../services/trialRequestedEvent';

/**
 * Which surface caused the trial dialog to open (analytics prop).
 * `post_signin` = re-offer after a sign-in that began with "Start Free Trial";
 * `post_signin_other` = one-time offer after a sign-in from any other surface
 * (Settings, "Have Pro? Sign in", …) whose account turned out to have no Pro.
 */
type TrialDialogTrigger = 'second_session' | 'card' | 'reminder' | 'post_signin' | 'post_signin_other';

/**
 * Session-scoped (per app boot) snooze for the first-run dialog: once the
 * user Escape/overlay-dismisses it, it stays closed until the next launch.
 * sessionStorage is fresh per BrowserWindow, so a new boot clears it — and
 * it survives hook remounts within a session.
 */
const SNOOZE_SESSION_KEY = 'sql-studio-trial-snoozed-session';
/** Persisted (localStorage) count of boots on which the first-run dialog was soft-dismissed. */
const SNOOZE_BOOTS_KEY = 'sql-studio-trial-snooze-boots';
/** After this many snoozed boots, auto-skip → the existing weekly-reminder cadence. */
const SNOOZE_BOOTS_TO_AUTO_SKIP = 3;

function isSnoozedThisSession(): boolean {
  try {
    return window.sessionStorage.getItem(SNOOZE_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Hook to manage trial onboarding dialog state.
 *
 * Shows the dialog when:
 * 1. First launch: trial state is 'not-started'
 * 2. Weekly reminder: trial was skipped AND shouldShowTrialReminder() returns true
 *
 * SERVER-BACKED TRIAL:
 * Starting a trial now requires authentication. The flow is:
 * 1. User clicks "Start Trial"
 * 2. If not authenticated, show AuthDialog
 * 3. After auth, call startServerTrial() to create server record
 * 4. Server returns signed entitlement token with trial dates
 * 5. Local state is updated from server response
 *
 * Note: In browser mode, trial dialogs are never shown. Browser mode provides
 * a full Pro experience for SQLite to showcase the app.
 *
 * @returns Dialog state and handlers
 */
export function useTrialOnboarding() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isReminder, setIsReminder] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [pendingTrialStart, setPendingTrialStart] = useState(false);

  const trialState = useLicenseStore((state) => state.getTrialState());
  const shouldShowReminder = useLicenseStore((state) => state.shouldShowTrialReminder());
  const firstSeenAt = useLicenseStore((state) => state.state.firstSeenAt);
  const startTrial = useLicenseStore((state) => state.startTrial);
  const startTrialFromServer = useLicenseStore((state) => state.startTrialFromServer);
  const skipTrial = useLicenseStore((state) => state.skipTrial);
  const dismissReminder = useLicenseStore((state) => state.dismissTrialReminder);
  const isInitialized = useLicenseStore((state) => state.isInitialized);
  const promptRequested = useTrialPromptStore((state) => state.requested);

  // Reactive Pro access status (correctly subscribes to all sources)
  const hasProAccess = useHasProAccess();
  // False until a signed-in user's subscription/entitlement has resolved —
  // before that, a Pro subscriber reads as Personal and the dialog would
  // flash open on boot, then auto-close once the subscription loads.
  const isProAccessReady = useProAccessReady();

  const user = useAuthStore((state) => state.user);
  const isAuthenticated = !!user;
  const isResolvingEntitlement = useAuthStore((state) => state.isResolvingEntitlement);

  // Track the user ID when auth dialog was opened for trial
  // This ensures we only start trial for the same user who initiated it
  const authDialogOpenedForUserId = useRef<string | null>(null);

  // Mirrors isDialogOpen so openTrialDialog only fires the "shown" analytics
  // event on a real closed→open transition (the gating effect re-runs on
  // dependency changes while the dialog is already open).
  const dialogOpenRef = useRef(false);
  useEffect(() => {
    dialogOpenRef.current = isDialogOpen;
  }, [isDialogOpen]);

  // Remembered so CTA click events carry the surface that opened the dialog.
  const lastTriggerRef = useRef<TrialDialogTrigger | null>(null);

  const openTrialDialog = useCallback((trigger: TrialDialogTrigger, reminder: boolean) => {
    setIsReminder(reminder);
    setIsDialogOpen(true);
    lastTriggerRef.current = trigger;
    if (!dialogOpenRef.current) {
      dialogOpenRef.current = true;
      window.electronAPI?.analytics
        ?.track('ui:trial_dialog_shown', { trigger })
        ?.catch(() => {});
    }
  }, []);

  const trackDialogClick = useCallback(
    (action: 'start_trial' | 'continue_personal' | 'dismiss' | 'snooze' | 'sign_in') => {
      window.electronAPI?.analytics
        ?.track('ui:trial_dialog_click', {
          action,
          trigger: lastTriggerRef.current ?? 'unknown',
        })
        ?.catch(() => {});
    },
    []
  );

  // Check on mount and when trial/subscription state changes
  useEffect(() => {
    // Browser mode: never show trial dialogs - users get Pro features for SQLite
    if (isBrowserMode()) {
      setIsDialogOpen(false);
      setIsReminder(false);
      return;
    }

    const isTestMode = typeof window !== 'undefined' && window.electronAPI?.testMode?.isTestMode?.() === true;
    const allowDialogInTests =
      typeof window !== 'undefined' &&
      window.localStorage.getItem('sql-studio-e2e-allow-trial-onboarding') === '1';

    if (isTestMode && !allowDialogInTests) {
      setIsDialogOpen(false);
      setIsReminder(false);
      return;
    }

    // Wait for store to be initialized (hydrated from persistence)
    if (!isInitialized) {
      return;
    }

    // If the user already has Pro access (trial/subscription/grace period/dev override), don't show onboarding
    // This check is reactive: dialog auto-closes when user upgrades to Pro
    if (hasProAccess) {
      setIsDialogOpen(false);
      setIsReminder(false);
      // Also clear any pending trial intent since user already has Pro
      setPendingTrialStart(false);
      setShowAuthDialog(false);
      clearPendingTrialIntent();
      return;
    }

    // Pro status still unknown (signed-in user's subscription not fetched yet).
    if (!isProAccessReady) {
      return;
    }

    if (trialState === 'not-started') {
      // First-time user. The dialog never auto-opens during the install-day
      // session — day 1 shows only the passive start-page trial card
      // (TrialStartCard); the dialog is due from the first later-day session.
      if (!isTrialOnboardingDue(firstSeenAt)) {
        return;
      }
      // Soft-dismissed (Escape/overlay) earlier this session — stay closed
      // until the next boot instead of re-opening on every effect re-run.
      if (isSnoozedThisSession()) {
        return;
      }
      openTrialDialog('second_session', false);
    } else if (trialState === 'skipped' && shouldShowReminder) {
      // Weekly reminder for users who skipped trial
      openTrialDialog('reminder', true);
    }
  }, [trialState, shouldShowReminder, isInitialized, hasProAccess, isProAccessReady, firstSeenAt, openTrialDialog]);

  // Explicit requests from the start-page trial card (day-1 surface):
  // clicking it always opens the matching dialog, regardless of the day gate.
  useEffect(() => {
    if (!promptRequested) return;
    useTrialPromptStore.getState().consume();
    if (promptRequested === 'sign-in') {
      recordAuthIntentSource('trial_card_signin');
      setShowAuthDialog(true);
      setPendingTrialStart(false);
      authDialogOpenedForUserId.current = null;
      return;
    }
    if (hasProAccess) return;
    openTrialDialog('card', trialState === 'skipped');
  }, [promptRequested, hasProAccess, trialState, openTrialDialog]);

  // Handle pending trial start after authentication completes
  useEffect(() => {
    // Only proceed if:
    // 1. There's a pending trial start intent
    // 2. User is now authenticated
    // 3. The auth dialog was opened specifically for trial (not closed by user)
    if (pendingTrialStart && isAuthenticated && authDialogOpenedForUserId.current === 'pending') {
      setPendingTrialStart(false);
      // Record the user ID who completed auth for trial
      authDialogOpenedForUserId.current = user?.id ?? null;
      void handleServerTrialStart();
    }
  }, [pendingTrialStart, isAuthenticated, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // One-time offer after a completed sign-in, for two persisted records
  // (the in-memory flow above never sees either):
  // - a trial INTENT that survived an app restart or a closed AuthDialog
  //   while sign-in finished in the browser ("Start Free Trial" was pressed);
  // - a plain sign-in from a surface that promised no trial (Settings,
  //   "Have Pro? Sign in", the dialog's "Already a Pro subscriber?" link)
  //   whose account resolved to no Pro — those users used to be left in
  //   Personal mode with nothing ever offering the trial.
  // Strictly a consent surface: the dialog opens once per record — nothing
  // auto-starts. Records are only consumed after every gate passes, so they
  // aren't burned while entitlement is still resolving.
  useEffect(() => {
    if (isBrowserMode()) return;
    const isTestMode =
      typeof window !== 'undefined' && window.electronAPI?.testMode?.isTestMode?.() === true;
    const allowDialogInTests =
      typeof window !== 'undefined' &&
      window.localStorage.getItem('sql-studio-e2e-allow-trial-onboarding') === '1';
    if (isTestMode && !allowDialogInTests) return;
    if (!isInitialized || !isProAccessReady) return;
    if (
      !shouldReofferTrialAfterSignIn({
        isAuthenticated,
        isResolvingEntitlement,
        hasProAccess,
        trialState,
        pendingTrialStartInFlight: pendingTrialStart,
      })
    ) {
      return;
    }
    const reason = consumeFreshTrialReoffer();
    if (!reason) return;
    // Signed in from the dialog's own "Sign in here" link: it is still open
    // and now shows the signed-in copy — no second open, no re-labelled trigger.
    if (dialogOpenRef.current) return;
    openTrialDialog(reason === 'trial_intent' ? 'post_signin' : 'post_signin_other', trialState === 'skipped');
  }, [
    isInitialized,
    isAuthenticated,
    isResolvingEntitlement,
    hasProAccess,
    isProAccessReady,
    trialState,
    pendingTrialStart,
    openTrialDialog,
  ]);

  /**
   * Start trial via server (requires auth).
   */
  const handleServerTrialStart = useCallback(async () => {
    // Check if auth is configured
    if (!isAuthConfigured()) {
      // Fall back to local trial for dev/testing when auth isn't configured
      console.log('[TrialOnboarding] Auth not configured, using local trial');
      startTrial();
      clearPendingTrialIntent();
      setIsDialogOpen(false);
      authDialogOpenedForUserId.current = null;
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await startServerTrial();

      if (!result.success) {
        // If the error is auth-related (e.g. expired token), show auth dialog
        // instead of a confusing error message
        const isAuthError =
          result.error?.toLowerCase().includes('not authenticated') ||
          result.error?.toLowerCase().includes('no access token');
        if (isAuthError) {
          setIsLoading(false);
          // Clear stale user so AuthDialog won't immediately auto-close
          // (user object exists but token is expired/invalid)
          useAuthStore.getState().setUser(null);
          recordPendingTrialIntent();
          recordAuthIntentSource('reauth');
          setShowAuthDialog(true);
          setPendingTrialStart(true);
          authDialogOpenedForUserId.current = 'pending';
          return;
        }
        setError(result.error || 'Failed to start trial');
        setIsLoading(false);
        return;
      }

      // Update local state with server trial data
      // ACCOUNT-BASED: Include user ID to prevent trial leakage between accounts
      if (result.trial) {
        startTrialFromServer(
          new Date(result.trial.started_at),
          new Date(result.trial.expires_at),
          user?.id,
          result.trial.is_new
        );
      } else if (result.payload?.mode === 'pro') {
        // User already has subscription - just close dialog
        console.log('[TrialOnboarding] User already has Pro subscription');
      }

      // Cache the signed token for offline use
      if (result.token && window.electronAPI?.licenseCache) {
        try {
          await window.electronAPI.licenseCache.updateCacheWithSignedToken(result.token);
        } catch (cacheErr) {
          console.warn('[TrialOnboarding] Failed to cache trial token:', cacheErr);
          // Don't fail - trial was created successfully
        }
      }

      clearPendingTrialIntent();
      setIsDialogOpen(false);
      setShowAuthDialog(false);
      authDialogOpenedForUserId.current = null;
    } catch (err) {
      console.error('[TrialOnboarding] Error starting trial:', err);
      setError(err instanceof Error ? err.message : 'Failed to start trial');
    } finally {
      setIsLoading(false);
    }
  }, [startTrial, startTrialFromServer]);

  const handleStartTrial = useCallback(() => {
    trackDialogClick('start_trial');
    // Trial-funnel stage "requested" (W4-B): the user asked for a trial here,
    // whether or not sign-in and the server call follow through. Compare with
    // `auth:trial_start_server { kind: 'new' }` for the completion rate.
    trackTrialRequested('onboarding', isAuthenticated);
    setError(null);

    const isTestMode = typeof window !== 'undefined' && window.electronAPI?.testMode?.isTestMode?.() === true;
    if (isTestMode) {
      startTrial();
      clearPendingTrialIntent();
      setIsDialogOpen(false);
      setShowAuthDialog(false);
      setPendingTrialStart(false);
      authDialogOpenedForUserId.current = null;
      return;
    }

    // If not authenticated, show auth dialog first. The intent is ALSO
    // persisted: sign-in finishes in an external browser, and the in-memory
    // flag dies if the user closes this dialog or the app restarts before the
    // deep link returns — the persisted record drives a one-time re-offer.
    if (!isAuthenticated && isAuthConfigured()) {
      recordPendingTrialIntent();
      recordAuthIntentSource('trial_dialog');
      setShowAuthDialog(true);
      setPendingTrialStart(true);
      // Mark that we opened auth for trial (no user yet)
      authDialogOpenedForUserId.current = 'pending';
      return;
    }

    // Already authenticated or auth not configured - start trial
    void handleServerTrialStart();
  }, [isAuthenticated, handleServerTrialStart, startTrial, trackDialogClick]);

  const handleContinuePersonal = useCallback(() => {
    trackDialogClick('continue_personal');
    if (isReminder) {
      // For reminder: just dismiss, don't call skipTrial again
      dismissReminder();
    } else {
      // First time: mark trial as skipped
      skipTrial();
    }
    setIsDialogOpen(false);
    setShowAuthDialog(false);
    setPendingTrialStart(false);
    authDialogOpenedForUserId.current = null;
    clearPendingTrialIntent();
  }, [isReminder, skipTrial, dismissReminder, trackDialogClick]);

  const handleDismissReminder = useCallback(() => {
    trackDialogClick('dismiss');
    dismissReminder();
    setIsDialogOpen(false);
    setShowAuthDialog(false);
    setPendingTrialStart(false);
    authDialogOpenedForUserId.current = null;
    clearPendingTrialIntent();
  }, [dismissReminder, trackDialogClick]);

  /**
   * Soft dismiss of the FIRST-RUN dialog (Escape/overlay-click): snooze for
   * this session, count the snoozed boot, and after a few snoozed boots stop
   * nagging by folding into the existing skipped→weekly-reminder cadence.
   */
  const handleSoftDismiss = useCallback(() => {
    trackDialogClick('snooze');
    setIsDialogOpen(false);
    setShowAuthDialog(false);
    setPendingTrialStart(false);
    authDialogOpenedForUserId.current = null;

    // Count at most one snoozed boot per session (the card can re-open the
    // dialog after a snooze; a second dismiss must not double-count).
    if (isSnoozedThisSession()) return;
    try {
      window.sessionStorage.setItem(SNOOZE_SESSION_KEY, '1');
    } catch {
      // sessionStorage unavailable — the dialog may re-open this session
    }
    let boots = 0;
    try {
      boots = parseInt(window.localStorage.getItem(SNOOZE_BOOTS_KEY) ?? '0', 10) || 0;
    } catch {
      // localStorage unavailable — treat as first snoozed boot
    }
    boots += 1;
    try {
      window.localStorage.setItem(SNOOZE_BOOTS_KEY, String(boots));
    } catch {
      // ignore persistence failure — worst case the counter restarts
    }
    if (boots >= SNOOZE_BOOTS_TO_AUTO_SKIP) {
      // The user has waved the dialog away repeatedly — respect that as a
      // skip so it stops re-opening every boot (reminder cadence takes over).
      skipTrial();
    }
  }, [skipTrial, trackDialogClick]);

  const handleAuthDialogClose = useCallback(() => {
    setShowAuthDialog(false);
    // CRITICAL: Clear pending trial intent when user closes the auth dialog
    // This prevents the trial from unexpectedly starting when user later signs in for another reason
    setPendingTrialStart(false);
    authDialogOpenedForUserId.current = null;
  }, []);

  /** Open auth dialog for existing Pro subscribers (no pending trial) */
  const handleSignIn = useCallback(() => {
    trackDialogClick('sign_in');
    recordAuthIntentSource('trial_dialog_signin');
    // Signing in as an existing subscriber is not trial intent — drop any
    // stale persisted intent so no surprise offer follows this sign-in.
    clearPendingTrialIntent();
    setShowAuthDialog(true);
    setPendingTrialStart(false);
    authDialogOpenedForUserId.current = null;
  }, [trackDialogClick]);

  return {
    /** Whether the trial dialog should be shown */
    isDialogOpen,
    /** Whether this is a reminder (vs first-time onboarding) */
    isReminder,
    /** Whether a trial start is in progress */
    isLoading,
    /** Error message if trial start failed */
    error,
    /** Whether the user is currently authenticated */
    isAuthenticated,
    /** Signed-in account's email, for the dialog's signed-in copy (null when unknown) */
    userEmail: user?.email ?? null,
    /** True while the signed-in account's entitlement is still being fetched */
    isResolvingEntitlement,
    /** Whether auth dialog should be shown */
    showAuthDialog,
    /** True when the open auth dialog is a step of STARTING A TRIAL (copy switch) */
    authDialogForTrial: pendingTrialStart,
    /** Handler for starting the trial */
    onStartTrial: handleStartTrial,
    /** Handler for choosing Personal mode */
    onContinuePersonal: handleContinuePersonal,
    /** Handler for dismissing the reminder */
    onDismissReminder: handleDismissReminder,
    /** Handler for soft-dismissing (snoozing) the first-run dialog */
    onSoftDismiss: handleSoftDismiss,
    /** Handler for closing auth dialog */
    onAuthDialogClose: handleAuthDialogClose,
    /** Handler for signing in as existing Pro user */
    onSignIn: handleSignIn,
  };
}
